import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import './SupportChat.css';

const SYSTEM_PROMPT = `You are CosmoEstates' helpful AI assistant. CosmoEstates is a Cyprus property marketplace serving Limassol, Paphos, Nicosia, Larnaca, and Famagusta. Available in English and Greek.

CRITICAL: BUYERS AND SELLERS ARE DIFFERENT AUDIENCES WITH DIFFERENT PRODUCTS.

Before answering any question about pricing, features, or "how do I do X", first identify whether the user is asking as a BUYER (looking for property) or a SELLER (has property to list). If unclear, ask.

FREE FOR EVERYONE (never upsell these):
- Listing a property is 100% free. Anyone can list at no cost.
- Browsing and searching properties is free.
- Contacting sellers or agents is free (via our messaging system).
- Saving favorites is free.

BUYER PRODUCT — Pro subscription
- €12.89/mo, €31.99/quarter, €112.99/year
- FOR BUYERS ONLY. Never recommend Pro to a seller.
- What it does: sends buyers automatic alerts when new listings match their saved searches; price drop alerts on saved properties; neighborhood intelligence tools for buyers evaluating areas.
- A seller does NOT need Pro to list, to get contacted, or to reach buyers. Never suggest Pro to someone asking about listing/selling.

SELLER PRODUCT — Boost
- €10.99 for 14 days (single listing)
- FOR SELLERS ONLY. Never recommend Boost to a buyer.
- What it does: takes an existing listing and puts it at the top of search results plus into the homepage Featured Listings carousel for 14 days. It's for sellers who already have a listing and want extra visibility.
- A buyer does NOT need Boost. Never suggest Boost to someone asking about finding or buying property.

HOW TO ANSWER COMMON QUESTIONS:

Q: "How do I list my property?"
A: "Listing is free. Click 'List Property' at the top of the page, add photos and details, and publish — it goes live immediately at no cost. If you want extra visibility later, you can boost your listing for €10.99 for 14 days."

Q: "How much does it cost to sell on CosmoEstates?"
A: "Listing is free. There's no fee to publish or contact interested buyers. If you want more visibility, Boost is €10.99 for 14 days and puts your listing at the top of search results."

Q: "Do I need Pro to list?"
A: "No. Pro is a buyer product for property alerts and saved searches. Sellers don't need it — listing is free."

Q: "How do I find properties?"
A: "Browsing is completely free. Use the search on our homepage or the /listings page. For automatic alerts when new listings match your criteria, Pro (€12.89/mo) gives you saved searches and matching notifications."

Q: "What's the difference between Pro and Boost?"
A: "Pro is for buyers — €12.89/mo for property alerts and saved searches. Boost is for sellers — €10.99 for 14 days to give an existing listing more visibility. Different products for different users."

Q: "How do I get more views on my listing?"
A: "Two things help most: complete details with 5+ photos, and boosting the listing (€10.99 for 14 days) which puts it at the top of search results and on our homepage carousel."

Q: "Can I contact a seller without paying?"
A: "Yes, contacting sellers is free. Open any listing and use the contact button — messages go through our platform."

CRITICAL ESCALATION RULES — these ALWAYS go to Contact Us:

1. PAYMENT/BILLING — "I was charged twice", "refund", "my Pro didn't activate", "boost not working", "Stripe issue"
   → Reply: "Payment questions need to be handled by our team directly. Please contact us here so we can check your account: https://cosmoestates.cy/contact"

2. ACCOUNT PROBLEMS — "can't log in", "deleted my listings", "forgot password issues", "delete my account"
   → Reply: "Account issues are best handled by our team. Please reach out here: https://cosmoestates.cy/contact"

3. SPECIFIC LISTING QUESTIONS — "is this villa available?", "can I view this property?", "is the price negotiable?"
   → Reply: "For questions about specific listings, please contact the agent directly using the contact details on that listing's page. They'll respond much faster than I can."

4. PARTNER/BROKER APPLICATIONS — "is my application approved?", "when can I start listing?"
   → Reply: "Application status updates come directly from our team. Please contact us to check your status: https://cosmoestates.cy/contact"

5. REPORTS / DISPUTES — "report fake listing", "scam", "this agent is bad", "wrong information"
   → Reply: "Reports go to our review team. Please submit details here so we can investigate: https://cosmoestates.cy/contact"

6. LEGAL/CONTRACTUAL — "is this legal?", "what are my rights?", "contract questions"
   → Reply: "I can't give legal advice. Please consult a Cyprus property lawyer or contact our team for guidance: https://cosmoestates.cy/contact"

7. PROPERTY VALUATIONS — "how much is my property worth?", "estimate my apartment"
   → Reply: "For a rough estimate, try our Property Price Estimate tool at https://cosmoestates.cy/valuation. Note this is an automated estimate, not a formal appraisal — for a certified valuation, please contact a licensed appraiser."

8. ANYTHING YOU'RE UNSURE ABOUT — if a question doesn't clearly match the topics you know
   → Reply: "I want to make sure you get the right answer. Please reach out to our team and they'll help directly: https://cosmoestates.cy/contact"

9. USER FRUSTRATED OR YOU'VE FAILED TWICE — if user repeats a question or expresses frustration
   → Reply: "I'm sorry I couldn't help. Our team will sort this out for you — please contact them here: https://cosmoestates.cy/contact"

TONE RULES:
- Warm, brief, helpful. Like a knowledgeable friend.
- Keep responses under 80 words.
- NEVER make up prices, dates, names, or facts.
- NEVER give legal, financial, or investment advice.
- NEVER cross-recommend: never suggest Pro to a seller, never suggest Boost to a buyer.
- ALWAYS prefer to escalate than to guess wrong.`;

const PRESET_QUESTIONS = [
  "How does CosmoEstates work?",
  "What's the difference between Free and Pro?",
  "How do I list my property?",
  "How does the valuation tool work?",
];

const WINDOW_MS = 3 * 60 * 60 * 1000;
const ANON_LIMIT = 10;
const LS_KEY = 'sc_rl';

// Simple markdown renderer
function renderMessage(text) {
  const lines = text.split('\n');
  const elements = [];
  let keyCounter = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) { elements.push(<br key={keyCounter++} />); continue; }
    if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
      const content = line.trim().slice(2);
      elements.push(
        <div key={keyCounter++} className="sc-bullet">
          <span className="sc-bullet__dot" />
          <span>{renderInline(content)}</span>
        </div>
      );
      continue;
    }
    elements.push(<div key={keyCounter++} className="sc-line">{renderInline(line)}</div>);
  }
  return elements;
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    return part;
  });
}

function RateLimitMessage({ quota, isPro }) {
  return (
    <div className="sc-ratelimit">
      <p className="sc-ratelimit__text">
        You've reached the chat limit ({quota.limit} messages every 3 hours).
      </p>
      <p className="sc-ratelimit__sub">Need help right away? Our team is quick to respond.</p>
      <div className="sc-ratelimit__btns">
        <a href="/contact" className="sc-ratelimit__btn sc-ratelimit__btn--primary">Contact Us →</a>
        {!isPro && (
          <a href="/pro" className="sc-ratelimit__btn sc-ratelimit__btn--secondary">Upgrade to Pro →</a>
        )}
      </div>
      {quota.minutesUntilReset > 0 && (
        <p className="sc-ratelimit__reset">
          Your limit resets in {quota.minutesUntilReset} minute{quota.minutesUntilReset !== 1 ? 's' : ''}.
        </p>
      )}
    </div>
  );
}

function BotIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/>
      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

function MinimiseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}

export default function SupportChat() {
  const { t } = useLanguage();
  const { user, hasProAccess } = useAuth();

  const [open, setOpen]       = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: t('supportChat.initialMessage') }]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [unread, setUnread]   = useState(0);
  const [error, setError]     = useState(null);
  const [quota, setQuota]     = useState({ used: 0, limit: 15, minutesUntilReset: 0 });

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);

  const msgLimit = hasProAccess ? 25 : (user ? 15 : ANON_LIMIT);

  useEffect(() => {
    if (open) { setUnread(0); setError(null); setTimeout(() => inputRef.current?.focus(), 150); }
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Load quota whenever user identity or Pro status changes
  useEffect(() => {
    loadQuota();
  }, [user?.id, hasProAccess]); // eslint-disable-line

  async function loadQuota() {
    const now = Date.now();
    if (!user) {
      const stored = JSON.parse(localStorage.getItem(LS_KEY) || '{"calls":[]}');
      const recent = stored.calls.filter(t => now - t < WINDOW_MS);
      const reset = recent.length > 0 ? Math.ceil((recent[0] + WINDOW_MS - now) / 60000) : 0;
      setQuota({ used: recent.length, limit: ANON_LIMIT, minutesUntilReset: Math.max(0, reset) });
      return;
    }
    try {
      const threeHoursAgo = new Date(now - WINDOW_MS).toISOString();
      const { count } = await supabase
        .from('ai_call_logs')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('feature', 'chatbot')
        .gte('created_at', threeHoursAgo);

      let resetMinutes = 0;
      const used = count || 0;
      if (used >= msgLimit) {
        const { data: oldest } = await supabase
          .from('ai_call_logs')
          .select('created_at')
          .eq('user_id', user.id)
          .eq('feature', 'chatbot')
          .gte('created_at', threeHoursAgo)
          .order('created_at', { ascending: true })
          .limit(1)
          .single();
        if (oldest) {
          const resetAt = new Date(oldest.created_at).getTime() + WINDOW_MS;
          resetMinutes = Math.max(1, Math.ceil((resetAt - now) / 60000));
        }
      }
      setQuota({ used, limit: msgLimit, minutesUntilReset: resetMinutes });
    } catch {
      // ai_call_logs table may not exist yet — fail silently
    }
  }

  async function logCall({ blocked, blockReason = null, inputTokens = null, outputTokens = null }) {
    try {
      if (user) {
        const cost = (inputTokens && outputTokens)
          ? inputTokens * 0.0000008 + outputTokens * 0.000004
          : null;
        await supabase.from('ai_call_logs').insert({
          user_id:      user.id,
          feature:      'chatbot',
          model:        'claude-haiku-4-5-20251001',
          input_tokens: inputTokens,
          output_tokens: outputTokens,
          cost_usd:     cost,
          blocked,
          block_reason: blockReason,
        });
      } else if (!blocked) {
        const stored = JSON.parse(localStorage.getItem(LS_KEY) || '{"calls":[]}');
        const now = Date.now();
        const recent = stored.calls.filter(t => now - t < WINDOW_MS);
        recent.push(now);
        localStorage.setItem(LS_KEY, JSON.stringify({ calls: recent }));
      }
    } catch {
      // silent — logging must never break chat
    }
  }

  const sendMessage = async (text) => {
    const userText = (text || input).trim();
    if (!userText || loading) return;
    setInput('');
    setError(null);

    const prevMessages = messages;
    const newMessages  = [...messages, { role: 'user', content: userText }];
    setMessages(newMessages);

    // ── Rate limit check ────────────────────────────────────────────────────
    const now = Date.now();
    let currentUsed = quota.used;

    if (user) {
      try {
        const { count } = await supabase
          .from('ai_call_logs')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('feature', 'chatbot')
          .gte('created_at', new Date(now - WINDOW_MS).toISOString());
        currentUsed = count || 0;
      } catch { /* table may not exist */ }
    } else {
      const stored = JSON.parse(localStorage.getItem(LS_KEY) || '{"calls":[]}');
      currentUsed = stored.calls.filter(t => now - t < WINDOW_MS).length;
    }

    if (currentUsed >= msgLimit) {
      await logCall({ blocked: true, blockReason: 'rate_limit' });
      await loadQuota();
      setMessages(prev => [...prev, { role: 'assistant', type: 'rate-limit', content: '' }]);
      return;
    }

    // ── Call Anthropic ───────────────────────────────────────────────────────
    setLoading(true);
    try {
      const response = await fetch('/api/chat-support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-haiku-4-5-20251001',
          max_tokens: 512,
          system: SYSTEM_PROMPT,
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
        }),
      });

      const rawText = await response.text();
      if (!response.ok) {
        let err = {};
        try { err = JSON.parse(rawText); } catch (_) {}
        throw new Error(err?.error?.message || `HTTP ${response.status}`);
      }

      const data  = JSON.parse(rawText);
      const reply = data.content?.[0]?.text || "Sorry, I couldn't get a response. Please try again.";

      await logCall({
        blocked: false,
        inputTokens:  data.usage?.input_tokens  || null,
        outputTokens: data.usage?.output_tokens || null,
      });
      setQuota(q => ({ ...q, used: q.used + 1 }));
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      if (!open) setUnread(prev => prev + 1);

    } catch (err) {
      console.error('SupportChat error:', err);
      setMessages(prevMessages);
      setInput(userText);
      setError(`AI service temporarily unavailable. Please try again in a moment.`);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handleInput = (e) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
  };

  const showQuota = quota.used > 5;
  const resetLabel = quota.minutesUntilReset > 0
    ? ` · resets in ${quota.minutesUntilReset}m`
    : '';

  return (
    <>
      {open && (
        <div className="sc-window">
          {/* Header */}
          <div className="sc-header">
            <div className="sc-header__left">
              <div className="sc-header__avatar">
                <BotIcon />
                <span className="sc-header__online" />
              </div>
              <div>
                <div className="sc-header__name">{t('supportChat.headerName')}</div>
                <div className="sc-header__status">{t('supportChat.headerStatus')}</div>
              </div>
            </div>
            <div className="sc-header__actions">
              <button className="sc-header__btn" onClick={() => setOpen(false)} title="Minimise"><MinimiseIcon /></button>
              <button className="sc-header__btn sc-header__btn--close" onClick={() => setOpen(false)} title="Close"><CloseIcon /></button>
            </div>
          </div>

          {/* Messages */}
          <div className="sc-messages">
            {messages.map((m, i) => (
              <div key={i} className={`sc-msg sc-msg--${m.role}`}>
                {m.role === 'assistant' && <div className="sc-msg__avatar"><BotIcon /></div>}
                <div className="sc-msg__bubble">
                  {m.type === 'rate-limit'
                    ? <RateLimitMessage quota={quota} isPro={!!hasProAccess} />
                    : renderMessage(m.content)}
                </div>
              </div>
            ))}

            {loading && (
              <div className="sc-msg sc-msg--assistant">
                <div className="sc-msg__avatar"><BotIcon /></div>
                <div className="sc-msg__bubble sc-msg__bubble--typing">
                  <span /><span /><span />
                </div>
              </div>
            )}

            {error && (
              <div className="sc-error">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Preset questions — only shown before first user message */}
          {messages.length === 1 && !loading && (
            <div className="chatbot__presets">
              {PRESET_QUESTIONS.map((q, i) => (
                <button key={i} className="chatbot__preset" onClick={() => sendMessage(q)}>{q}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="sc-input-row">
            <textarea
              ref={inputRef}
              className="sc-input"
              placeholder={t('supportChat.placeholder')}
              value={input}
              onChange={handleInput}
              onKeyDown={handleKey}
              rows={1}
            />
            <button
              className={`sc-send ${input.trim() ? 'sc-send--active' : ''}`}
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
            >
              <SendIcon />
            </button>
          </div>

          {/* Quota indicator */}
          {showQuota && (
            <div className="sc-quota">
              {quota.used}/{quota.limit} messages used{resetLabel}
            </div>
          )}

          {/* Permanent Contact Us link */}
          <div className="sc-contact-fallback">
            Need a human?{' '}
            <a href="/contact">Contact our support team →</a>
          </div>
        </div>
      )}

      {/* FAB */}
      <button
        className={`sc-fab ${open ? 'sc-fab--open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Open support chat"
      >
        {open ? <CloseIcon /> : (
          <div className="sc-fab__icon">
            <BotIcon />
            <span className="sc-fab__pulse" />
          </div>
        )}
        {!open && unread > 0 && <span className="sc-fab__badge">{unread}</span>}
      </button>
    </>
  );
}
